/**
 * Per-topic source citations and last-reviewed metadata.
 *
 * Keep this file curated and conservative - every entry should be a primary or
 * widely-trusted secondary source a teacher can verify in under 60 seconds.
 * Bump `lastReviewed` whenever the topic's lesson plan, quiz, or pathway copy
 * changes materially.
 */
export interface TopicSource {
  label: string;
  /** Optional URL. Leave undefined for offline textbook references. */
  url?: string;
  /** Short note: what this source backs up. */
  note?: string;
}

export interface TopicProvenance {
  /** ISO date (YYYY-MM-DD) of last accuracy review. */
  lastReviewed: string;
  /** Initials or short name of the reviewer. */
  reviewer?: string;
  sources: TopicSource[];
}

export const TOPIC_PROVENANCE: Record<string, TopicProvenance> = {
  "lab-foundations": {
    lastReviewed: "2026-05-05",
    reviewer: "ElementPad editorial",
    sources: [
      {
        label: "OSHA Hazard Communication Standard (29 CFR 1910.1200)",
        url: "https://www.osha.gov/hazcom",
        note: "SDS structure and required hazard pictograms.",
      },
      {
        label: "OpenStax Chemistry 2e - Ch. 1.6 Measurement Uncertainty, Accuracy, and Precision",
        url: "https://openstax.org/books/chemistry-2e/pages/1-6-measurement-uncertainty-accuracy-and-precision",
      },
      {
        label: "Texas Essential Knowledge and Skills, §112.35 - Chemistry",
        url: "https://tea.texas.gov/about-tea/laws-and-rules/sboe-rules-tac/sboe-tac-currently-in-effect/ch112c.pdf",
      },
    ],
  },
  "periodic-table": {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 6.5 Periodic Variations in Element Properties",
        url: "https://openstax.org/books/chemistry-2e/pages/6-5-periodic-variations-in-element-properties",
      },
      {
        label: "IUPAC Periodic Table of the Elements (2021)",
        url: "https://iupac.org/what-we-do/periodic-table-of-elements/",
      },
    ],
  },
  "atomic-structure": {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 6.1–6.3 Electronic Structure of Atoms",
        url: "https://openstax.org/books/chemistry-2e/pages/6-1-electromagnetic-energy",
      },
      {
        label: "NIST Atomic Spectra Database",
        url: "https://www.nist.gov/pml/atomic-spectra-database",
      },
    ],
  },
  bonding: {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 7 Chemical Bonding and Molecular Geometry",
        url: "https://openstax.org/books/chemistry-2e/pages/7-introduction",
      },
      {
        label: "Zumdahl & Zumdahl, Chemistry 10e - Ch. 8–9",
        note: "Lewis structures, VSEPR, intermolecular forces.",
      },
    ],
  },
  stoichiometry: {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 4 Stoichiometry of Chemical Reactions",
        url: "https://openstax.org/books/chemistry-2e/pages/4-introduction",
      },
      {
        label: "NIST Standard Reference Database - Atomic Weights",
        url: "https://www.nist.gov/pml/atomic-weights-and-isotopic-compositions-relative-atomic-masses",
      },
    ],
  },
  "gas-laws": {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 9 Gases",
        url: "https://openstax.org/books/chemistry-2e/pages/9-introduction",
      },
      {
        label: "NIST Reference on Constants - Gas Constant R",
        url: "https://physics.nist.gov/cgi-bin/cuu/Value?r",
      },
    ],
  },
  solutions: {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 11 Solutions and Colloids",
        url: "https://openstax.org/books/chemistry-2e/pages/11-introduction",
      },
      {
        label: "CRC Handbook of Chemistry and Physics - Solubility tables",
        note: "Reference values for saturation and temperature dependence.",
      },
    ],
  },
  "acid-base": {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 14 Acid–Base Equilibria",
        url: "https://openstax.org/books/chemistry-2e/pages/14-introduction",
      },
      {
        label: "IUPAC Compendium of Chemical Terminology (Gold Book) - pH",
        url: "https://goldbook.iupac.org/terms/view/P04524",
      },
    ],
  },
  thermochemistry: {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 5 Thermochemistry",
        url: "https://openstax.org/books/chemistry-2e/pages/5-introduction",
      },
      {
        label: "NIST Chemistry WebBook - Standard Enthalpies of Formation",
        url: "https://webbook.nist.gov/chemistry/",
      },
    ],
  },
  nuclear: {
    lastReviewed: "2026-05-05",
    sources: [
      {
        label: "OpenStax Chemistry 2e - Ch. 21 Nuclear Chemistry",
        url: "https://openstax.org/books/chemistry-2e/pages/21-introduction",
      },
      {
        label: "IAEA Live Chart of Nuclides",
        url: "https://www-nds.iaea.org/relnsd/vcharthtml/VChartHTML.html",
      },
    ],
  },
};

/** Where errata reports go. Update if you change the inbox. */
export const ERRATA_EMAIL = "errata@elementpad.net";